import React, { useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import Button from "components/lib/Shared/Button";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import AssignPermissionsSelection from "components/lib/Shared/CompanyDashboard/AssignPermission/Selection";
import { CORPORATES } from "components/PLCTABS/SFLdata/config/constants";
import {
  createPermission,
  editPermission,
  getPermissions,
  resetPermissionSlice,
} from "state/slices/permission";
import { useToast } from "hooks/useToast";
import {
  createRole,
  editRole,
  getRoles,
  resetRoleSlice,
} from "state/slices/role";
import _omit from "lodash/omit";
import {
  getOrganizationEmployeeAsync,
  removeEmployeeAsync,
} from "state/slices/employee";

export default function SubRowAsync({
  visibleColumns,
  row,
  forEmployee = false,
}) {
  const closeSubRowRef = useRef(null);
  const { organization } = useSelector((state) => state.organizationsSlice);
  const { permissions, getPermissionsStatus } = useSelector(
    (state) => state.permissionSlice
  );
  const { roles, getRolesStatus } = useSelector((state) => state.roleSlice);

  const { toast } = useToast(5000);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(resetPermissionSlice());
    dispatch(resetRoleSlice());
    dispatch(
      getPermissions({
        ...(forEmployee
          ? { employee: row.original._id }
          : {
              [CORPORATES.includes(row.original.idType)
                ? "corporate"
                : "individual"]: row.original._id,
            }),
        organization: organization._id,
        status: "Confirmed",
      })
    );
    dispatch(
      getRoles({
        ...(forEmployee
          ? { employee: row.original._id }
          : {
              [CORPORATES.includes(row.original.idType)
                ? "corporate"
                : "individual"]: row.original._id,
            }),
        organization: organization._id,
        status: "Confirmed",
      })
    );
  }, []);
  if (
    getPermissionsStatus.status === "loading" ||
    getRolesStatus.status === "loading"
  ) {
    return (
      <tr className="bg-[#EDF0F5]">
        <td colSpan={visibleColumns.length} className="px-9">
          <div className="flex justify-center mx-auto my-2 ">
            <SpinnerIcon className="text-gray-400" />
          </div>
        </td>
      </tr>
    );
  }

  let grantedPermission = permissions?.[0] ?? {};
  let grantedRole = roles?.[0] ?? {};
  const handleAccess = async (values) => {
    const payload = _omit(values, ["_id", "createdAt", "updatedAt"]);
    const { dashboardPermissions, dashboardRoles, ...restValues } = payload;
    const toastMessage =
      grantedPermission?._id || grantedRole._id
        ? "Updated access successfully."
        : "Allowed access successfully.";
    let isSuccess = true;
    if (grantedPermission?._id) {
      try {
        await dispatch(
          editPermission({
            permissionId: grantedPermission._id,
            data: {
              dashboardPermissions,
              ...restValues,
            },
          })
        );
      } catch (error) {
        isSuccess = false;
        toast(
          "error",
          error?.message ?? "Something went wrong please try again."
        );
      }
    } else {
      try {
        await dispatch(
          createPermission({
            data: {
              dashboardPermissions,
              ...restValues,
              [CORPORATES.includes(row.original.idType)
                ? "corporate"
                : "individual"]: row.original._id,
              organization: organization._id,
              status: "Confirmed",
            },
          })
        );
      } catch (error) {
        isSuccess = false;
        toast(
          "error",
          error?.message ?? "Something went wrong please try again."
        );
      }
    }
    if (grantedRole?._id) {
      try {
        await dispatch(
          editRole({
            roleId: grantedRole._id,
            data: {
              dashboardRoles,
              ...restValues,
            },
          })
        );
      } catch (error) {
        isSuccess = false;
        toast(
          "error",
          error?.message ?? "Something went wrong please try again."
        );
      }
    } else {
      try {
        await dispatch(
          createRole({
            data: {
              dashboardRoles,
              ...restValues,
              [CORPORATES.includes(row.original.idType)
                ? "corporate"
                : "individual"]: row.original._id,
              organization: organization._id,
              status: "Confirmed",
            },
          })
        );
      } catch (error) {
        isSuccess = false;
        toast(
          "error",
          error?.message ?? "Something went wrong please try again."
        );
      }
    }

    if (isSuccess) {
      toast("success", toastMessage);
      closeSubRowRef.current.click();
    }
  };

  const handleDelete = async () => {
    try {
      const empres = await dispatch(
        removeEmployeeAsync({ employeeId: row.original._id })
      );
      const orgres = await dispatch(
        getOrganizationEmployeeAsync({ organizationId: organization._id })
      );
      toast("success", "Employee Deleted successfully.");
      closeSubRowRef.current.click();
    } catch (error) {
      toast(
        "error",
        error?.message ?? "Something went wrong please try again."
      );
    }
  };

  let defaultAccessTypeSchema = {
    startDate: null,
    endDate: null,
    revokedAt: null,
    duration: "indefinite",
  };

  if (grantedPermission?.accessType) {
    const { accessType, startDate, endDate, revokedAt, ...rest } =
      grantedPermission;
    defaultAccessTypeSchema.duration = accessType ?? "indefinite";
    defaultAccessTypeSchema.startDate = startDate ?? null;
    defaultAccessTypeSchema.endDate = endDate ?? null;
    defaultAccessTypeSchema.revokedAt = revokedAt ?? null;
    grantedPermission = {
      ...rest,
    };
  }

  return (
    <tr className="bg-[#EDF0F5]">
      <td colSpan={visibleColumns.length} className="px-9">
        <div className="flex justify-between items-center mt-2">
          {/* button to close sub row programmatically */}
          {row.isExpanded && (
            <Button
              ref={closeSubRowRef}
              variant="text"
              className="space-x-0 gap-0"
              onClick={(e) => row.getToggleRowExpandedProps().onClick?.(e)}
              {...row.getToggleRowExpandedProps()}
            ></Button>
          )}
        </div>
        <p className="text-gray-500 text-sm mb-1">Access</p>
        <hr />
        <AssignPermissionsSelection
          initialValues={{
            accessType: {
              standardUser: defaultAccessTypeSchema,
              orgAdmin: defaultAccessTypeSchema,
              brsOps: defaultAccessTypeSchema,
              superAdmin: defaultAccessTypeSchema,
              billing: defaultAccessTypeSchema,
            },
            dashboardPermissions: [],
            dashboardRoles: [],
            ...grantedPermission,
            ...grantedRole,
          }}
          hasRoles={true}
          handleSubmit={(values) => {
            handleAccess(values);
          }}
          submitBtnLabel={
            grantedPermission?._id || grantedRole._id
              ? "Update Rights"
              : "Grant Rights"
          }
          loadingText={
            grantedPermission?._id || grantedRole._id
              ? "Updating..."
              : "Granting..."
          }
          forEmployee={forEmployee}
          handleDelete={handleDelete}
          showOrgAdminRoles={true}
        />
      </td>
    </tr>
  );
}
